import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useRef, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { AlertTriangle, Info } from "lucide-react";
import { useEscalateToCentralAPI } from "@/app/hooks/api_hooks/LabReport/useEscalateToCentralAPI";
import { useRejectReportAPI } from "@/app/hooks/api_hooks/LabReport/useRejectReportAPI";
import { useLabReportStatsListAPI } from "@/app/hooks/api_hooks/LabReport/useLabReportStatsListAPI";
import { useLabReportFilterListAPI } from "@/app/hooks/api_hooks/LabReport/useLabReportFilterListAPI";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { useRouter } from "@/app/hooks/useRouter";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { ILabReportListState } from "@/app_redux/reducers/slices/auth/lab_report_slice";
import { Controller, useForm } from "react-hook-form";
import clsx from "clsx";
import { useNavigate, useLocation } from "react-router";
import { useLabPartnerTrfRejectReasonAPI } from "@/app/hooks/api_hooks/LabReport/useLabPartnerTrfRejectReasonAPI";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";

interface IEscalateReportModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  requisitionId: string | null;
  testDetails?: any;
  invoiceId?: string;
}

const EscalateReportModal: React.FC<IEscalateReportModalProps> = ({
  open,
  setOpen,
  requisitionId,
  testDetails,
  invoiceId,
}) => {
  const cancelButtonRef = useRef(null);

  const { getRouteKey } = useRouter();
  const { customerId } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id")).routeState.state as ICustomerProfileState;
  const { labReportListFilter } = useRouteInfo(getRouteKey("LAB_REPORT", "id")).routeState.state as ILabReportListState;
  const { execute: fetchTrfRejectReason, trfRejectReason } = useLabPartnerTrfRejectReasonAPI();

  console.log("trfRejectReason", trfRejectReason)
  const { execute: escalateAPI } = useEscalateToCentralAPI();
  const { execute: rejectAPI } = useRejectReportAPI();
  const location = useLocation();
  const isLabTestUrl = location.pathname.includes("lab-test");

  const fetchStatsList = useLabReportStatsListAPI();
  const { execute: fetchLabReports } = useLabReportFilterListAPI();
  const [loader, setLoader] = useState(false);
  const [showOtherTextarea, setShowOtherTextarea] = useState(false);



  const formHook = useForm<any>({
    mode: "onChange",
    defaultValues: {
      shortage_reason: "",
      short: false,
    },
  });

  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const queryClinics = queryParams.get("clinic_ids");
  const queryShift = queryParams.get("shift") ?? "day";
  const processId = queryParams.get("closing_process");
  const isClinicView = queryParams.get("is_clinic");
  const city_id = queryParams.get("city_id");

  const getSource = () => {
    if (location.pathname.includes("/incoming")) {
      return "external";
    } else if (location.pathname.includes("/internal") || location.pathname.includes("/external")) {
      return "internal";
    }
    return "external";
  };

  const LabType = (tab: string) => {
    if (tab === "incoming") {
      return 2;
    } else if (tab === "external") {
      return 1;
    } else if (tab === "internal") {
      return 3;
    }
  };

  const handleClick = () => {
    const source = getSource();
    setLoader(true);
    rejectAPI(requisitionId, source, formHook.watch("shortage_reason")).then(({ status }: any) => {
      setLoader(false);
      if (status) {
        setOpen(false);
        processId &&
          navigate(
            `/app/closing/day-closing?closing_process=${processId}&city_id=${city_id}&clinic_ids=${queryClinics}&is_clinic=${isClinicView}&shift=${queryShift}&t=${Date.now()}`,
          );
        if (processId) {
          // Day closing: the navigate above bumps `t`, which makes the closing tab
          // refetch its own list. Fetching here too would overwrite it.
        } else if (isLabTestUrl) {
          const constructedBody: any = {
            start_date: (labReportListFilter as any)?.start_date,
            end_date: (labReportListFilter as any)?.end_date,
            customer_id: customerId,
            type: 3,
          };
          fetchStatsList(constructedBody);
          fetchLabReports(constructedBody);
        } else {
          let payload: any = {
            start_date: (labReportListFilter as any)?.start_date,
            end_date: (labReportListFilter as any)?.end_date,
            status_ids: labReportListFilter?.status_ids,
            clinic_ids: labReportListFilter?.clinic_ids,
            type: LabType(location.pathname.split("/").pop()),
          };
          if (labReportListFilter?.lab_partner_ids && labReportListFilter?.lab_test_type) {
            payload = {
              ...payload,
              lab_partner_ids: labReportListFilter?.lab_partner_ids,
              lab_test_type: labReportListFilter?.lab_test_type,
            };
          }
          fetchStatsList(payload);
          fetchLabReports(payload);
        }
      }
    });
  };

  const isFormValid = formHook.watch("shortage_reason")?.trim() && formHook.watch("short");

  useEffect(() => {
    if (open) {
      fetchTrfRejectReason();
    }
  }, [open]);

  useEffect(() => {
    formHook.register("shortage_reason", {
      required: "Please select a rejection reason",
    });
  }, [formHook]);

  const rejectReasonOptions =
    [
      ...(trfRejectReason || []).map((reason: string) => ({
        id: reason,
        title: reason,
      })),
      {
        id: "__OTHER__",
        title: "Other",
      },
    ];


  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-y-auto"
        initialFocus={cancelButtonRef}
        onClose={() => !loader && setOpen(false)}
      >
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
          </Transition.Child>

          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block align-bottom bg-white rounded-3xl text-left overflow-visible shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:w-full sm:max-w-xl">
              {/* Premium Header */}
              <div className="bg-[#F9FAFB] px-6 py-4 flex items-center justify-between border-b border-gray-100 sticky top-0 z-10">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 bg-black rounded-xl">
                    <AlertTriangle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-gray-900 leading-tight">Escalate Report</h2>
                    <p className="text-[11px] text-gray-500 font-medium uppercase tracking-wider mt-0.5">
                      Requisition Management
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  disabled={loader}
                  className="p-1.5 rounded-full hover:bg-gray-200 transition-colors bg-gray-100 disabled:opacity-50"
                  onClick={() => {
                    formHook.reset();
                    setOpen(false);
                  }}
                >
                  <XMarkIcon className="h-5 w-5 text-gray-600" />
                </button>
              </div>

              <div className="p-6 space-y-6 font-primary">
                {/* Standardized Detail Section */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <div className="space-y-1">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Requisition ID</p>
                    <p className="text-xs font-medium text-gray-900">{testDetails?.requisition_number || "NA"}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Customer Name</p>
                    <p className="text-xs font-medium text-gray-900 line-clamp-1">
                      {testDetails?.customer_details?.name || "NA"}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Pet Details</p>
                    <p className="text-xs font-medium text-gray-900">
                      {testDetails?.pet_details?.name || "NA"}
                      {testDetails?.pet_details?.pet_species_name && (
                        <span className="text-gray-400 font-normal ml-1">
                          ({testDetails?.pet_details?.pet_species_name?.toLowerCase() === "canine" ? "Dog" : "Cat"})
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Lab Partner</p>
                    <p className="text-xs font-medium text-gray-900 line-clamp-1" title={testDetails?.lab_partner_name}>
                      {testDetails?.lab_partner_name || "NA"}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase">Test Name</p>
                    <p className="text-xs text-gray-900 line-clamp-1" title={testDetails?.test_name}>
                      {testDetails?.test_name || "NA"}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase">Doctor</p>
                    <p
                      className="text-xs font-medium text-gray-900 line-clamp-1"
                      title={testDetails?.staff_details?.name}
                    >
                      {testDetails?.staff_details?.name || "NA"}
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5 ml-1">
                      Reason for Escalation <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <div className="w-full">
                        <SearchableSelectMenu
                          register={formHook.register("shortage_reason", {
                            required: "Please select a rejection reason",
                          })}
                          selectItems={rejectReasonOptions}
                          placeholder="Select rejection reason"
                          containerClassName="w-full"
                          listBoxClassName="w-full"
                          className="text-gray-800"
                          showDropdownIcon
                          showTooltips
                          showTypedErrors={false}
                          fieldError={formHook.formState.errors.shortage_reason}
                          errorMessages={[]}
                          defaultSelected={rejectReasonOptions.find(
                            (r) => r.id === formHook.watch("shortage_reason")
                          )}
                          onSelectItem={(item) => {
                            if (!item) return;
                            if (item.id === "__OTHER__") {
                              setShowOtherTextarea(true);
                              formHook.setValue("shortage_reason", "", {
                                shouldDirty: true,
                                shouldValidate: true,
                              });
                            } else {
                              setShowOtherTextarea(false);
                              formHook.setValue("shortage_reason", item.id, {
                                shouldDirty: true,
                                shouldValidate: true,
                              });
                            }
                          }}
                        />
                      </div>
                    </div>

                    {showOtherTextarea && (
                      <Controller
                        name="shortage_reason"
                        control={formHook.control}
                        rules={{
                          required: "Please enter the reason",
                          validate: (value) =>
                            value.trim() !== "" || "Reason cannot be blank",
                        }}
                        render={({ field }) => (
                          <textarea
                            {...field}
                            rows={4}
                            disabled={loader}
                            placeholder="Please specify the reason..."
                            className="w-full p-3 border border-gray-200 rounded-xl text-sm
                          focus:ring-black focus:border-black transition-all
                          outline-none resize-none placeholder:text-gray-400"
                          />
                        )}
                      />
                    )}

                    {formHook.formState.errors.shortage_reason && (
                      <p className="text-red-500 text-[11px] mt-1.5 font-medium px-1">
                        {formHook.formState.errors.shortage_reason.message as string}
                      </p>
                    )}
                  </div>

                  <div className="flex items-start gap-3 px-1">
                    <div className="flex items-center h-5">
                      <input
                        id="short"
                        type="checkbox"
                        disabled={loader}
                        {...formHook.register("short", { required: true })}
                        className="h-4 w-4 rounded border-gray-300 text-black focus:ring-black cursor-pointer disabled:opacity-50"
                      />
                    </div>
                    <label
                      htmlFor="short"
                      className="text-[13px] text-gray-600 cursor-pointer select-none leading-tight font-medium disabled:opacity-50"
                    >
                      I confirm and agree to escalate this report to central management.
                    </label>
                  </div>

                  <div className="flex items-center gap-1.5 px-3 py-2 bg-amber-50 rounded-xl border border-amber-100">
                    <Info className="w-4 h-4 text-amber-600 shrink-0" />
                    <p className="text-[10px] text-amber-700 font-medium italic">
                      This action will notify the central support team for further investigation.
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex gap-3">
                <button
                  type="button"
                  disabled={loader}
                  className="flex-1 px-4 py-2.5 text-xs font-bold text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-100 transition-colors disabled:opacity-50"
                  onClick={() => {
                    formHook.reset();
                    setOpen(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  disabled={loader || !isFormValid}
                  className={clsx(
                    "flex-1 px-4 py-2.5 text-xs font-bold rounded-xl flex items-center justify-center gap-2 transition-all shadow-sm active:scale-95",
                    {
                      "bg-gray-200 text-gray-400 cursor-not-allowed shadow-none": loader || !isFormValid,
                      "bg-black text-white hover:bg-gray-900": !loader && isFormValid,
                    },
                  )}
                  onClick={() => handleClick()}
                >
                  {loader ? (
                    <div className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      <span>Escalating...</span>
                    </div>
                  ) : (
                    "Escalate Now"
                  )}
                </button>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default EscalateReportModal;
